import React, {useCallback, useEffect, useState} from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import {Box} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import {useTranslation} from "react-i18next";
import {v4 as uuidv4} from "uuid";
import CustomEmpty from "../../custom-empty";

const $ = window.jQuery;

let timeoutID = null;
const SCROLLBARS_TIMEOUT = 100;
let intervalID = null;
const SCROLLBARS_INTERVAL = 200;

const WHEEL_DELTA = 1;

const SLIDER_THICKNESS = 32;
const SLIDER_MARGIN = 4;

const SLIDER_RAIL_COLOR = "#cfcfcf";
const SLIDER_THUMB_COLOR = "#808080";

const sliderSlotProps = {
  root: {
    sx: {
      color: SLIDER_THUMB_COLOR,
      width: "2px",
      height: "2px"
    }
  },
  rail: {
    sx: {
      color: SLIDER_RAIL_COLOR,
      opacity: 1
    }
  },
  thumb: {
    sx: {
      width: "16px",
      height: "16px"
    }
  }
};

const sliderButtonStyle = {
  width: `${SLIDER_THICKNESS}px`,
  height: `${SLIDER_THICKNESS}px`,
  padding: "0px",
  "& svg": {
    width: `${SLIDER_THICKNESS}px`,
    height: `${SLIDER_THICKNESS}px`
  }
};

const spinnerOverlayStyle = {
  width: "100%",
  height: "100%",
  position: "absolute",
  top: "0px",
  left: "0px"
};

const CustomSlider = ({
  uuid,
  orientation,
  value,
  maxValue,
  onChange,
  onIncrement,
  ticks,
  isVerticalScrollbarVisible
}) => {
  const {t} = useTranslation();

  useEffect(() => {
    const onMouseUp = () => {
      clearInterval(intervalID);
    };
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, []);

  useEffect(() => {
    $(".MuiSlider-thumb").attr("tabindex", "-1");
  });

  return orientation === "vertical" ? (
    <div style={{width: SLIDER_THICKNESS, height: "100%", display: "inline-block", verticalAlign: "top"}}>
      <IconButton
        sx={sliderButtonStyle}
        onMouseDown={() => {
          if (value > 0) {
            onIncrement(false, 1);
            intervalID = setInterval(() => onIncrement(false, 1), SCROLLBARS_INTERVAL);
          }
        }}
        disabled={value === 0}
        tabIndex={-1}
      >
        <ArrowDropUpIcon />
      </IconButton>
      <Slider
        id={`sliders__custom-sliders__vertical__${uuid}`}
        orientation="vertical"
        aria-label={t("components.table.scrollbars.vertical.ariaLabel")}
        track={false}
        value={ticks - value}
        min={0}
        max={ticks}
        onChange={(ev, newValue) => {
          const invertedValue = ticks - newValue;
          onChange(invertedValue);
        }}
        slotProps={sliderSlotProps}
        style={{
          height: `calc(100% - ${SLIDER_THICKNESS + SLIDER_THICKNESS + SLIDER_MARGIN + SLIDER_MARGIN}px)`,
          padding: "0 15px",
          margin: `${SLIDER_MARGIN}px 0`
        }}
      />
      <IconButton
        sx={sliderButtonStyle}
        onMouseDown={() => {
          if (value < maxValue - 1) {
            onIncrement(true, 1);
            intervalID = setInterval(() => onIncrement(true, 1), SCROLLBARS_INTERVAL);
          }
        }}
        disabled={value >= maxValue - 1}
        tabIndex={-1}
      >
        <ArrowDropDownIcon />
      </IconButton>
    </div>
  ) : (
    <div style={{width: "100%", height: SLIDER_THICKNESS, display: "flex"}}>
      <IconButton
        sx={sliderButtonStyle}
        onMouseDown={() => {
          onIncrement(false, 1);
          intervalID = setInterval(() => onIncrement(false, 1), SCROLLBARS_INTERVAL);
        }}
        disabled={value === 0}
        tabIndex={-1}
      >
        <ArrowLeftIcon fontSize="small" />
      </IconButton>
      <Slider
        id={`sliders__custom-sliders__horizontal__${uuid}`}
        orientation="horizontal"
        aria-label={t("components.table.scrollbars.horizontal.ariaLabel")}
        track={false}
        value={value}
        min={0}
        max={ticks}
        onChange={(ev, newValue) => {
          onChange(newValue);
        }}
        slotProps={sliderSlotProps}
        style={{
          width: `calc(100% - ${
            SLIDER_THICKNESS +
            SLIDER_THICKNESS +
            SLIDER_MARGIN +
            SLIDER_MARGIN +
            (isVerticalScrollbarVisible ? SLIDER_THICKNESS : 0)
          }px)`,
          padding: "15px 0",
          margin: `0 ${SLIDER_MARGIN}px`
        }}
      />
      <IconButton
        sx={sliderButtonStyle}
        onMouseDown={() => {
          onIncrement(true, 1);
          intervalID = setInterval(() => onIncrement(true, 1), SCROLLBARS_INTERVAL);
        }}
        disabled={value >= maxValue - 1}
        tabIndex={-1}
      >
        <ArrowRightIcon />
      </IconButton>
      {isVerticalScrollbarVisible && <div style={{width: SLIDER_THICKNESS, height: SLIDER_THICKNESS}} />}
    </div>
  );
};

const Scrollbars = ({
  children,
  verticalValue,
  verticalMaxValue,
  verticalTicks,
  onVerticalScroll,
  isVerticalScrollbarVisible,
  horizontalValue,
  horizontalMaxValue,
  horizontalTicks,
  onHorizontalScroll,
  isHorizontalScrollbarVisible,
  disableWheelZoom
}) => {
  const {t} = useTranslation();

  const [uuid] = useState(uuidv4());

  const [row, setRow] = useState(verticalValue);
  const [col, setCol] = useState(horizontalValue);

  const [isPaginating, setIsPaginating] = useState(false);

  const handleSliderScroll = (value, isVertical) => {
    if (isVertical) {
      setRow(value);
      if (value !== row) {
        setIsPaginating(true);
      }
    } else {
      setCol(value);
      if (value !== col) {
        setIsPaginating(true);
      }
    }
  };

  const getIncrementedValue = (prevValue, delta, isIncrement, limit) =>
    isIncrement
      ? prevValue + delta > limit
        ? limit
        : prevValue + delta
      : prevValue - delta < limit
        ? limit
        : prevValue - delta;

  const handleIncrement = (isIncrement, isVertical, delta, limit) =>
    isVertical
      ? setRow(prevRow =>
          isIncrement
            ? getIncrementedValue(prevRow, delta, isIncrement, limit || verticalMaxValue - 1)
            : getIncrementedValue(prevRow, delta, isIncrement, limit || 0)
        )
      : setCol(prevCol =>
          isIncrement
            ? getIncrementedValue(prevCol, delta, isIncrement, limit || horizontalMaxValue - 1)
            : getIncrementedValue(prevCol, delta, isIncrement, limit || 0)
        );

  const handleWheelScroll = useCallback(
    ev => {
      const isVertical = ev.wheelDeltaY !== 0;
      const wheelDelta = isVertical
        ? ev.wheelDeltaY > 0
          ? -WHEEL_DELTA
          : WHEEL_DELTA
        : ev.wheelDeltaX > 0
          ? -WHEEL_DELTA
          : WHEEL_DELTA;

      const isBetween = (val, min, max) => val >= min && val < max;

      if (isVertical) {
        setRow(prevRow => {
          const newRow = isBetween(prevRow + wheelDelta, 0, verticalMaxValue) ? prevRow + wheelDelta : prevRow;
          return isVerticalScrollbarVisible ? newRow : prevRow;
        });
      } else {
        setCol(prevCol => {
          const newCol = isBetween(prevCol + wheelDelta, 0, horizontalMaxValue) ? prevCol + wheelDelta : prevCol;
          return isHorizontalScrollbarVisible ? newCol : prevCol;
        });
      }
    },
    [isVerticalScrollbarVisible, isHorizontalScrollbarVisible, verticalMaxValue, horizontalMaxValue, setRow, setCol]
  );

  useEffect(() => {
    if (!disableWheelZoom) {
      document.getElementById(`scrollbars-container__${uuid}`).addEventListener("wheel", handleWheelScroll);
    }
    return () => {
      if (document.getElementById(`scrollbars-container__${uuid}`)) {
        document.getElementById(`scrollbars-container__${uuid}`).removeEventListener("wheel", handleWheelScroll);
      }
    };
  }, [uuid, disableWheelZoom, handleWheelScroll]);

  useEffect(() => {
    if (timeoutID) {
      clearTimeout(timeoutID);
    }

    timeoutID = setTimeout(() => {
      onVerticalScroll(row);
      onHorizontalScroll(col);
      setIsPaginating(false);
    }, SCROLLBARS_TIMEOUT);
  }, [row, col, onVerticalScroll, onHorizontalScroll, setIsPaginating]);

  useEffect(() => {
    setRow(verticalValue);
    setCol(horizontalValue);
  }, [verticalValue, horizontalValue, setRow, setCol]);

  return (
    <div id={`scrollbars-container__${uuid}`} style={{width: "100%", height: "100%"}}>
      <div style={{width: "100%", height: `calc(100% - ${isHorizontalScrollbarVisible ? SLIDER_THICKNESS : 0}px)`}}>
        <div
          style={{
            width: `calc(100% - ${isVerticalScrollbarVisible ? SLIDER_THICKNESS : 0}px)`,
            height: "100%",
            display: "inline-block",
            verticalAlign: "top",
            position: "relative"
          }}
        >
          <Box sx={spinnerOverlayStyle} style={{filter: isPaginating ? "blur(1px)" : ""}}>
            {children}
          </Box>
          {isPaginating && (
            <Box sx={spinnerOverlayStyle}>
              <CustomEmpty
                text={t("components.table.scrollbars.loading") + "..."}
                textStyle={{fontWeight: "bold"}}
                image={<CircularProgress style={{color: "#ffffff"}} />}
                style={{
                  color: "#ffffff",
                  backgroundColor: "rgba(0, 0, 0, 0.1)"
                }}
              />
            </Box>
          )}
        </div>
        {isVerticalScrollbarVisible && (
          <CustomSlider
            uuid={uuid}
            orientation="vertical"
            value={row}
            maxValue={verticalMaxValue}
            onChange={value => handleSliderScroll(value, true)}
            onIncrement={(isIncrement, delta, limit) => handleIncrement(isIncrement, true, delta, limit)}
            ticks={verticalTicks}
            isVerticalScrollbarVisible={isVerticalScrollbarVisible}
          />
        )}
      </div>
      {isHorizontalScrollbarVisible && (
        <CustomSlider
          uuid={uuid}
          orientation="horizontal"
          value={col}
          maxValue={horizontalMaxValue}
          onChange={value => handleSliderScroll(value, false)}
          onIncrement={(isIncrement, delta, limit) => handleIncrement(isIncrement, false, delta, limit)}
          ticks={horizontalTicks}
          isVerticalScrollbarVisible={isVerticalScrollbarVisible}
        />
      )}
    </div>
  );
};

export default Scrollbars;
